// Turns a message's plain text into what Mail shows: whitespace from table
// layouts and invisible preheader filler gone, the quoted earlier messages split
// off, and every web address a link labelled with its host. It never produces
// markup: the result is text and links that React renders as text.

export type Span = { text: string } | { href: string; host: string };
export interface MailText { body: Span[][]; quoted: Span[][] }

// Zero-width and soft characters senders pad a preview line with.
const INVISIBLE = /[­͏؜ᅟᅠ឴឵᠎​-‏‪-‮⁠-⁤ㅤ﻿ﾠ]/g;
// An address, with the brackets plain-text mail wraps it in: "( url )", "(url)", "[url]", "<url>".
const LINK = /[(<[]\s*(https?:\/\/[^\s<>()[\]]+)\s*[)>\]]|(https?:\/\/[^\s<>()[\]]+)/g;
// Where the quoted earlier message starts: "On … wrote:", Outlook's header, or a run of "> " lines to the end.
const QUOTE_HEAD = /^(On\s.+\swrote:|-{2,}\s*Original Message\s*-{2,}|_{10,})$/i;

export function mailText(raw: string): MailText {
  const lines = raw.replace(/\r\n?/g, '\n').replace(INVISIBLE, '').replace(/[ \t]/g, ' ').split('\n').map((l) => l.replace(/ {2,}/g, ' ').trim());
  const cut = quoteStart(lines);
  return { body: paragraphs(lines.slice(0, cut)), quoted: paragraphs(lines.slice(cut)) };
}

function quoteStart(lines: string[]): number {
  for (let i = 0; i < lines.length; i++) {
    // Gmail wraps a long "On … wrote:" over two lines.
    if (QUOTE_HEAD.test(lines[i]!) || (/^On\s/.test(lines[i]!) && QUOTE_HEAD.test(`${lines[i]} ${lines[i + 1] ?? ''}`))) return i;
    if (lines[i]!.startsWith('>') && lines.slice(i).every((l) => l === '' || l.startsWith('>'))) return i;
  }
  return lines.length;
}

// Blank lines separate paragraphs; a line break inside one is kept as "\n".
function paragraphs(lines: string[]): Span[][] {
  const out: Span[][] = [];
  let para: string[] = [];
  const flush = () => { if (para.length) out.push(spans(para.join('\n'))); para = []; };
  for (const l of lines) if (l) para.push(l); else flush();
  flush();
  return out;
}

function spans(text: string): Span[] {
  const out: Span[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK)) {
    // A sentence's full stop or comma after an address is not part of it.
    const href = (m[1] ?? m[2])!.replace(/[.,;:!?'"]+$/, '');
    const host = hostOf(href);
    if (!host) continue;
    const end = m[1] ? m.index! + m[0].length : m.index! + href.length;
    if (m.index! > last) out.push({ text: text.slice(last, m.index).replace(/ +$/, ' ') });
    out.push({ href, host });
    last = end;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  // "X ( url )LinkedIn ( url )": the bracket kept the words apart; the link must too.
  return out.map((s, i) => ('text' in s && i > 0 && /^[\p{L}\p{N}]/u.test(s.text) ? { text: ` ${s.text}` } : s));
}

// One line of a message for a list or a folded message: invisible filler gone,
// whitespace collapsed, links and quoting left as written.
export function snippetText(raw: string, max = 160): string {
  const line = raw.replace(INVISIBLE, '').replace(/\s+/g, ' ').trim();
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

function hostOf(href: string): string | null {
  try { return new URL(href).hostname.replace(/^www\./, '') || null; } catch { return null; }
}
